import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { styled } from 'styled-components';
import {
  Button,
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  Icon,
  Popover,
  PopoverRoot,
  PopoverTrigger,
  Typography,
  tk,
} from '@sunwebgroup/ui-components';

import { useIntl } from '@/intl';

interface FilterGroupProps {
  title: string;
  explanation?: string;
  explanationReadMoreLink?: string;
  explanationReadMoreLinkText?: string;
  defaultOpen?: boolean;
}

export default function FilterGroup({
  children,
  title,
  explanation,
  explanationReadMoreLink,
  explanationReadMoreLinkText,
  defaultOpen,
}: PropsWithChildren<FilterGroupProps>) {
  const intl = useIntl();
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <Wrapper open={open} onOpenChange={setOpen}>
      <GroupHeader>
        <CollapsibleTrigger asChild>
          {/* Needed, because can't place a <button> inside a <button> */}
          <ClickArea aria-label={title} />
        </CollapsibleTrigger>
        <Typography $variant="body-lg-bold">{title}</Typography>
        {explanation && (
          <PopoverRoot>
            <PopoverTrigger asChild>
              <ExplanationButton $size="small" $hierarchy="tertiary">
                {intl.formatMessage({ id: 'search_filter_explanation_text' })}
              </ExplanationButton>
            </PopoverTrigger>

            <Popover
              title={title}
              content={explanation}
              readMoreLink={explanationReadMoreLink}
              readMoreLinkText={explanationReadMoreLinkText}
              align="center"
            />
          </PopoverRoot>
        )}
        <StyledIcon glyph={open ? 'IconChevronUp' : 'IconChevronDown'} />
      </GroupHeader>
      <Collapsible>{children}</Collapsible>
    </Wrapper>
  );
}

const Wrapper = styled(CollapsibleRoot)`
  margin-bottom: ${tk('spacing_lg')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${tk('spacing_md')};
  position: relative;
`;

const ExplanationButton = styled(Button)`
  color: ${tk('theme_color_neutral_500')};
  text-decoration: underline;
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
`;

const StyledIcon = styled(Icon)`
  align-self: center;
  margin-left: auto;
`;

const ClickArea = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%; // needed for Firefox
`;
